import {
    EntityTypeName,
    IPrPayslipEmploymentItemEntity,
    IPrPayslipEmploymentSubitemEntity,
    IPrPayslipItemEntity,
    PrPayslipItemEntity
} from "@odata/GeneratedEntityTypes";
import {
    PrPayslipEmploymentItemTypeCode,
    PrPayslipEmploymentSubitemTypeCode,
    PrPayslipItemTypeCode,
    PrPayslipSubitemTypeCode
} from "@odata/GeneratedEnums";
import { getEnumDisplayValue } from "@odata/GeneratedEnums.utils";
import { TFormTableListValue } from "@pages/payroll/payslip/formTableList/FormTableList";
import {
    ITotalOverviewArgs,
    RowValueType,
    TFilterSubitemsFn,
    TPayslipItem,
    TPayslipOverviewFormatterFn,
    TPayslipOverviewRowDef,
    TPayslipSubItemDef
} from "@pages/payroll/payslip/overview/TotalOverview.utils";
import { validityRangeFormatter } from "@pages/payroll/payslip/Payslip.utils";
import i18next from "i18next";

import { DASH_CHARACTER } from "../../../../constants";
import { TRecordAny } from "../../../../global.types";
import { getUtcDayjs } from "../../../../types/Date";
import { FormStorage } from "../../../../views/formView/FormStorage";

interface IPayslipOverviewSectionsDef {
    title?: string;
    rows: TPayslipOverviewRowDef[];
}

function getRowHeaderLabel(key: string): string {
    return `${i18next.t(`Payslip:FormOverviews.${key}`).toString()}:`;
}

function getHeaderRowDef(additionalLabels: string[], visibilityCondition?: string[]): TPayslipOverviewRowDef {
    return {
        label: getRowHeaderLabel("Name"),
        value: getRowHeaderLabel("Amount"),
        isHeader: true,
        isSubRow: true,
        subItems: additionalLabels.map((key): TPayslipSubItemDef => ({ value: getRowHeaderLabel(key) })),
        visibleIfContains: visibilityCondition
    };
}


const SALE_SUBITEM_ID = "Sale";
const filterOutSaleSubitemsIfNotPresent: TFilterSubitemsFn = (defs, args) => {
    const items = args.employment.Items.filter(i => i.TypeCode === PrPayslipEmploymentItemTypeCode.SocialInsurancePaidByEmployee || i.TypeCode === PrPayslipEmploymentItemTypeCode.SocialInsurancePaidByEmployer);
    const hasSaleSubitems = items?.some(item => {
        return (item as TPayslipItem).Subitems?.some(sub => [PrPayslipEmploymentSubitemTypeCode.CzSocialInsEmployerDiscountPerc, PrPayslipEmploymentSubitemTypeCode.CzSocialInsEmployeeRetireeDiscPerc].includes(sub.TypeCode as PrPayslipEmploymentSubitemTypeCode));
    });
    if (hasSaleSubitems) {
        return defs;
    }
    return defs?.filter(def => {
        return def?.id !== PrPayslipEmploymentSubitemTypeCode.CzSocialInsEmployerDiscountPerc &&
            def?.id !== PrPayslipEmploymentSubitemTypeCode.CzSocialInsEmployeeRetireeDiscPerc &&
            def?.id !== SALE_SUBITEM_ID;
    });
};

const deductionTypeFormatter: TPayslipOverviewFormatterFn = (item, valueFormatter, _, args) => {
    return (item as IPrPayslipItemEntity)?.Deduction?.Type?.Name;
};

const isTaxesSubheaderVisible = (storage: FormStorage, args: ITotalOverviewArgs) => {
    return args.payslipItems?.some(i => i.TypeCode === PrPayslipItemTypeCode.WithholdingTax) &&
        args.payslipItems?.some(i => i.TypeCode === PrPayslipItemTypeCode.IncomeTaxNet);
};

/**
 * Configuration for basic payslip calculations block related to employee
 */
export const getTotalOverviewPayslipSectionsDef: () => IPayslipOverviewSectionsDef[] = () => ([
    {
        rows: [
            // **** Basic calculations block ****
            { id: PrPayslipItemTypeCode.TaxableIncomeTotal, isHeader: true },

            {
                label: i18next.t("Payslip:FormOverviews.AdvanceIncomeTax"),
                isHeader: true,
                formatter: () => "",
                isVisible: isTaxesSubheaderVisible
            },
            { id: PrPayslipItemTypeCode.AdvanceIncomeTaxCalculationBasis },
            { id: PrPayslipItemTypeCode.AdvanceIncomeTaxCalculationBasisRounded },
            { id: PrPayslipItemTypeCode.AdvanceIncomeTaxBeforeAdvantages },
            { id: PrPayslipItemTypeCode.IncomeTaxReduction },
            { id: PrPayslipItemTypeCode.IncomeTaxAdvantages },
            { id: PrPayslipItemTypeCode.IncomeTaxNet },
            { id: PrPayslipItemTypeCode.IncomeTaxBonus },

            {
                label: i18next.t("Payslip:FormOverviews.WithholdingTax"),
                isHeader: true,
                formatter: () => "",
                isVisible: isTaxesSubheaderVisible
            },
            { id: PrPayslipItemTypeCode.WithholdingIncomeTaxCalculationBasis },
            { id: PrPayslipItemTypeCode.WithholdingTax },

            // **** Health Insurance ****
            { id: PrPayslipItemTypeCode.HealthInsuranceTotal, isHeader: true },
            getHeaderRowDef(["Base", "Size"], [PrPayslipItemTypeCode.HealthInsurancePaidByEmployee, PrPayslipItemTypeCode.HealthInsurancePaidByEmployer,
                PrPayslipItemTypeCode.HealthInsuranceMinimumSurchargePaidByEmployee]),
            {
                id: PrPayslipItemTypeCode.HealthInsurancePaidByEmployee,
                subItems: [{
                    formatter: healthInsuranceBaseFormatter,
                    valueType: RowValueType.Currency
                },
                    {
                        id: PrPayslipSubitemTypeCode.HealthInsuranceEmployeePercentage,
                        valueType: RowValueType.Percentage
                    }
                ]
            },
            {
                id: PrPayslipItemTypeCode.HealthInsurancePaidByEmployer,
                subItems: [{
                    formatter: healthInsuranceBaseFormatter,
                    valueType: RowValueType.Currency
                },
                    {
                        id: PrPayslipSubitemTypeCode.HealthInsuranceEmployerPercentage,
                        valueType: RowValueType.Percentage
                    }
                ]
            },
            { id: PrPayslipItemTypeCode.HealthInsuranceMinimumSurchargePaidByEmployee, isSubRow: true },
            { id: PrPayslipItemTypeCode.HealthInsuranceMinimumSurchargePaidByEmployer, isSubRow: true },

            // **** Social Insurance ****
            {
                id: PrPayslipItemTypeCode.SocialInsuranceTotal,
                isHeader: true,
                isVisible: (storage) => {
                    return storage?.data.entity.Employments?.length > 1;
                }
            },

            // **** Net Salary ****
            { id: PrPayslipItemTypeCode.NetSalary, isHeader: true },
            { id: PrPayslipItemTypeCode.NetIncome, isHeader: true },
            {
                id: [
                    PrPayslipItemTypeCode.NetSalary,
                    PrPayslipItemTypeCode.Illness,
                    PrPayslipItemTypeCode.IncomeTaxBonus,
                    PrPayslipItemTypeCode.FinancialComponentsSubjectOnlyToIncomeTax,
                    PrPayslipItemTypeCode.RemoteWorkBenefit,
                    PrPayslipItemTypeCode.AnnualIncomeTaxSettlement,
                    PrPayslipItemTypeCode.LumpSumMealBenefit,
                    PrPayslipItemTypeCode.FinancialBenefitsTotal,
                    PrPayslipItemTypeCode.EmergencyTotal,
                ],
                isSubRow: true,
                headerRow: getHeaderRowDef([])
            },

            // **** Deductions ****
            { id: PrPayslipItemTypeCode.DeductionCalculationBasis, isHeader: true },
            { id: PrPayslipItemTypeCode.DeductionTotal, isHeader: true },
            getHeaderRowDef([PrPayslipItemEntity.Type], [
                PrPayslipItemTypeCode.CustomDeduction,
                PrPayslipItemTypeCode.MealVoucherEmployeeDeduction,
                PrPayslipItemTypeCode.OwnDeduction,
                PrPayslipItemTypeCode.CourtOrderedDeduction,
                PrPayslipItemTypeCode.InsolvencyAdministratorReward]),
            {
                id: [
                    PrPayslipItemTypeCode.CustomDeduction,
                    PrPayslipItemTypeCode.MealVoucherEmployeeDeduction,
                    PrPayslipItemTypeCode.OwnDeduction,
                    PrPayslipItemTypeCode.CourtOrderedDeduction,
                    PrPayslipItemTypeCode.InsolvencyAdministratorReward
                ],
                isSubRow: true,
                subItems: [{ id: PrPayslipItemEntity.TypeCode, formatter: deductionTypeFormatter }]
            },

            // **** Undeducted child support ****
            { id: PrPayslipItemTypeCode.UndeductedTotal, isHeader: true },
            getHeaderRowDef([PrPayslipItemEntity.Type], [PrPayslipItemTypeCode.OwedRegularChildSupport]),
            {
                id: PrPayslipItemTypeCode.OwedRegularChildSupport,
                isSubRow: true,
                subItems: [{ id: PrPayslipItemEntity.TypeCode, formatter: deductionTypeFormatter }]
            },

            { id: PrPayslipItemTypeCode.SalaryAdvanceTotal, isHeader: true },
            {
                id: PrPayslipItemTypeCode.SalaryAdvance,
                isSubRow: true,
                headerRow: getHeaderRowDef([]),
                label: (item) => (item as IPrPayslipItemEntity).SalaryAdvance?.NumberOurs,
            },
            // **** Liabilities and Receivables ****
            { id: PrPayslipItemTypeCode.ReceivablesTotal, isHeader: true },
            {
                id: PrPayslipItemTypeCode.Receivables,
                headerRow: getHeaderRowDef([]),
                label: (item) => (item as IPrPayslipItemEntity)?.Document?.NumberOurs,
                isSubRow: true,
            },
            { id: PrPayslipItemTypeCode.LiabilitiesTotal, isHeader: true },
            {
                id: PrPayslipItemTypeCode.Liabilities,
                headerRow: getHeaderRowDef([]),
                label: (item) => (item as IPrPayslipItemEntity)?.Document?.NumberOurs,
                isSubRow: true,
            },
        ]
    },
    {
        rows: [
            // **** Amount to pay employee ****
            { id: PrPayslipItemTypeCode.AmountToPayEmployee, isHeader: true },
            // **** Non-financial income ****
            { id: PrPayslipItemTypeCode.NonfinancialIncomeTotal },
        ]
    },
]);


const absenceAmountFormatter: TPayslipOverviewFormatterFn = (item, valueFormatter) => {
    const empItem = item as unknown as IPrPayslipEmploymentItemEntity;
    return valueFormatter ? valueFormatter(empItem.IntValue) : empItem.IntValue?.toString();
};

const filterOutAbsenceSizeSubitems: TFilterSubitemsFn = (defs, args) => {
    const hasSizeSubitems = args.employment?.Items.some(item => {
        return (item as TPayslipItem).TypeCode === PrPayslipEmploymentItemTypeCode.PaidTimeOffSummerCamp;
    });

    if (hasSizeSubitems) {
        return defs;
    }
    return defs?.filter(def => {
        return def?.id !== PrPayslipEmploymentSubitemTypeCode.CzPaidTimeOffSummerCampHourlyCompensation
            && def?.id !== "Size";
    });
};

const socialInsuranceBaseFormatter: TPayslipOverviewFormatterFn = (item, valueFormatter, value: TFormTableListValue, args) => {
    const calc = args.employment.Items.find(x => x.TypeCode === PrPayslipEmploymentItemTypeCode.BasisForSocialInsuranceCalculation)?.DecimalValue ?? 0;
    return valueFormatter ? valueFormatter(calc) : calc.toString();
};

const healthInsuranceBaseFormatter: TPayslipOverviewFormatterFn = (item, valueFormatter, value: TFormTableListValue, args) => {
    const calc = args.payslipItems.find(x => x.TypeCode === PrPayslipItemTypeCode.BasisForHealthInsuranceCalculation)?.DecimalValue ?? 0;
    return valueFormatter ? valueFormatter(calc) : calc.toString();
};

const ExtraSalaryBaseItemTypes = [
    PrPayslipEmploymentItemTypeCode.Night,
    PrPayslipEmploymentItemTypeCode.Weekend,
    PrPayslipEmploymentItemTypeCode.Overtime,
    PrPayslipEmploymentItemTypeCode.HarshWorkingEnvironment,
    PrPayslipEmploymentItemTypeCode.Holiday,
];
const extraSalaryCountFormatter: TPayslipOverviewFormatterFn = (item, valueFormatter) => {
    const empItem = item as unknown as IPrPayslipEmploymentItemEntity;
    return valueFormatter ? valueFormatter(empItem.IntValue) : empItem.IntValue?.toString();
};
const extraSalaryPercentageFormatter: TPayslipOverviewFormatterFn = (item, valueFormatter) => {
    const subItem = (item as unknown as IPrPayslipEmploymentItemEntity)?.Subitems?.[0];
    return valueFormatter ? valueFormatter(subItem.DecimalValue) : subItem.DecimalValue?.toString();
};
const extraSalaryAbsValueFormatter: TPayslipOverviewFormatterFn = (item, valueFormatter) => {
    const empItem = item as unknown as IPrPayslipEmploymentItemEntity;
    return valueFormatter ? valueFormatter(empItem.IntValue) : empItem.IntValue?.toString();
};

export const isStartOfEndOfMonth = (date: Date, validType: "ValidFrom" | "ValidTo"): boolean => {
    const fn = validType === "ValidFrom" ? "startOf" : "endOf";
    return getUtcDayjs(date).isSame(getUtcDayjs(date)[fn]("month"), "day");
};

const _hasDifferentValidityDate = (item: TPayslipItem, validType: "ValidFrom" | "ValidTo"): boolean => {
    const subItem = ((item as TPayslipItem).Subitems as TRecordAny[])?.find(i => i.TypeCode?.includes(validType)) as IPrPayslipEmploymentSubitemEntity;
    return !!subItem && !!subItem.DateValue && !isStartOfEndOfMonth(subItem.DateValue, validType);
};

const filterOutValidityColumns: TFilterSubitemsFn = (defs, args) => {
    const hasNonFullMonthValidity = args.filteredItems?.some(item => {
        return _hasDifferentValidityDate(item as TPayslipItem, "ValidFrom") || _hasDifferentValidityDate(item as TPayslipItem, "ValidTo");
    });

    // if there is some subitem with different validity, we filter nothing, otherwise we filter out items which includes ValidFrom/To in id
    if (!hasNonFullMonthValidity) {
        defs = defs?.filter(def => {
            return !((def?.id as string)?.includes("Validity"));
        });
    }

    return defs;
};

const salaryValidityFormatter: TPayslipOverviewFormatterFn = (item) => {
    const empItem = item as unknown as IPrPayslipEmploymentItemEntity;
    const validFrom = empItem.Subitems.find(x => x.TypeCode === PrPayslipEmploymentSubitemTypeCode.CzSalaryWorkedValidFrom)?.DateValue;
    const validTo = empItem.Subitems.find(x => x.TypeCode === PrPayslipEmploymentSubitemTypeCode.CzSalaryWorkedValidTo)?.DateValue;
    return validityRangeFormatter(validFrom, validTo);
};

const workedHoursFormatter: TPayslipOverviewFormatterFn = (item, valueFormatter) => {
    const empItem = item as unknown as IPrPayslipEmploymentItemEntity;
    return empItem.IntValue ? valueFormatter(empItem.IntValue) : DASH_CHARACTER;
};

const limitItems = [
    PrPayslipEmploymentItemTypeCode.MealVouchersUnderLimit,
    PrPayslipEmploymentItemTypeCode.MealVouchersOverLimit,
    PrPayslipEmploymentItemTypeCode.LumpSumMealBenefitUnderLimit,
    PrPayslipEmploymentItemTypeCode.LumpSumMealBenefitOverLimit,
    PrPayslipEmploymentItemTypeCode.BenefitsFullyExempt,
    PrPayslipEmploymentItemTypeCode.BenefitsExemptToLimitOf1_2Avg_Salary,
    PrPayslipEmploymentItemTypeCode.BenefitsExemptToLimitOfAvg_Salary,
    PrPayslipEmploymentItemTypeCode.BenefitsOverLimit,
    PrPayslipEmploymentItemTypeCode.BenefitsNotExempt,
    PrPayslipEmploymentItemTypeCode.RemoteWorkBenefitUnderLimit,
    PrPayslipEmploymentItemTypeCode.RemoteWorkBenefitOverLimit
];

/**
 * Configuration for basic payslip calculations block related to employment
 */
export const getTotalOverviewEmploymentSectionsDef: () => IPayslipOverviewSectionsDef = () => (
    {
        rows: [
            { id: PrPayslipEmploymentItemTypeCode.PlannedWorkHours, valueType: RowValueType.HoursFromMinutes },
            { id: PrPayslipEmploymentItemTypeCode.AverageSalary, valueType: RowValueType.CurrencyPerHour },

            // **** Salary Components ****
            { id: PrPayslipEmploymentItemTypeCode.GrossSalaryEmployment, isHeader: true },
            { id: PrPayslipEmploymentItemTypeCode.FinancialIncomeFullyTaxed, isHeader: true },
            {
                id: [
                    PrPayslipEmploymentItemTypeCode.SalaryForHoursWorked,
                    PrPayslipEmploymentItemTypeCode.SalaryPercentageBonus,
                    PrPayslipEmploymentItemTypeCode.SalaryBonus,
                    PrPayslipEmploymentItemTypeCode.OtherSalaryFullyTaxed,
                ],
                headerRow: {
                    label: getRowHeaderLabel("Name"),
                    value: getRowHeaderLabel("Amount"),
                    isHeader: true,
                    isSubRow: true,
                    subItems: [{
                        id: "Validity",
                        value: getRowHeaderLabel("Validity")
                    }, {
                        id: "Worked",
                        value: getRowHeaderLabel("Worked")
                    }, {
                        value: getRowHeaderLabel("Size")
                    }],
                    filterSubItems: filterOutValidityColumns
                },
                isSubRow: true,
                filterSubItems: filterOutValidityColumns,
                subItems: [
                    {
                        id: "Validity",
                        formatter: salaryValidityFormatter
                    },
                    { formatter: workedHoursFormatter, valueType: RowValueType.HoursFromMinutes },
                    { id: PrPayslipEmploymentSubitemTypeCode.CzSalaryWorkedBase }
                ]
            },
            {
                id: [
                    PrPayslipEmploymentItemTypeCode.MonthlyCompensation,
                ],
                isSubRow: true,
            },
            // **** Salary Components non-financial ****
            { id: PrPayslipEmploymentItemTypeCode.SalaryInKindFullyTaxedTotal, isHeader: true },
            {
                id: [
                    PrPayslipEmploymentItemTypeCode.SalaryInKindFullyTaxed
                ],
                headerRow: getHeaderRowDef([]),
                isSubRow: true,
            },
            // **** Salary Components outside Insurance ****
            { id: PrPayslipEmploymentItemTypeCode.OtherSalaryIncomeTaxOnlyTotal, isHeader: true },
            {
                id: [
                    PrPayslipEmploymentItemTypeCode.OtherSalaryIncomeTaxOnly,
                    PrPayslipEmploymentItemTypeCode.SeverancePay,
                    PrPayslipEmploymentItemTypeCode.TerminationSettlement,
                    PrPayslipEmploymentItemTypeCode.RetirementBenefit,
                    PrPayslipEmploymentItemTypeCode.CompensationForLossOfMedicalFitnessSect_271ca,
                ],
                headerRow: getHeaderRowDef([]),
                isSubRow: true,
            },
            // **** Salary Components non-financial outside insurance ****
            { id: PrPayslipEmploymentItemTypeCode.SalaryInKindIncomeTaxOnlyTotal, isHeader: true },
            {
                id: [
                    PrPayslipEmploymentItemTypeCode.SalaryInKindIncomeTaxOnly
                ],
                headerRow: getHeaderRowDef([]),
                isSubRow: true,
            },

            // **** Absences ****
            {
                id: PrPayslipEmploymentItemTypeCode.PaidAbsences,
                isHeader: true,
                visibleIfContains: [
                    PrPayslipEmploymentItemTypeCode.Vacation,
                    PrPayslipEmploymentItemTypeCode.SickDay,
                    PrPayslipEmploymentItemTypeCode.LegislativeAbsencePaid,
                    PrPayslipEmploymentItemTypeCode.PaidTimeOffSummerCamp,
                    PrPayslipEmploymentItemTypeCode.CustomAbsencePaid,
                    PrPayslipEmploymentItemTypeCode.HolidayCompensation
                ]
            },
            {
                // header row for Employee and Employer social insurance
                label: getRowHeaderLabel("Name"),
                value: getRowHeaderLabel("Amount"),
                isHeader: true,
                isSubRow: true,
                subItems: [{
                    id: "Hours",
                    value: getRowHeaderLabel("Hours")
                }, {
                    id: "Size",
                    value: getRowHeaderLabel("Size")
                }],
                filterSubItems: filterOutAbsenceSizeSubitems,
                visibleIfContains: [
                    PrPayslipEmploymentItemTypeCode.Vacation,
                    PrPayslipEmploymentItemTypeCode.SickDay,
                    PrPayslipEmploymentItemTypeCode.LegislativeAbsencePaid,
                    PrPayslipEmploymentItemTypeCode.PaidTimeOffSummerCamp,
                    PrPayslipEmploymentItemTypeCode.CustomAbsencePaid,
                    PrPayslipEmploymentItemTypeCode.HolidayCompensation
                ]
            },
            {
                id: [
                    PrPayslipEmploymentItemTypeCode.Vacation,
                    PrPayslipEmploymentItemTypeCode.SickDay,
                    PrPayslipEmploymentItemTypeCode.LegislativeAbsencePaid,
                    PrPayslipEmploymentItemTypeCode.PaidTimeOffSummerCamp,
                    PrPayslipEmploymentItemTypeCode.CustomAbsencePaid,
                    PrPayslipEmploymentItemTypeCode.HolidayCompensation
                ],
                isSubRow: true,
                valueType: RowValueType.Currency,
                filterSubItems: filterOutAbsenceSizeSubitems,
                subItems: [
                    { formatter: absenceAmountFormatter, valueType: RowValueType.HoursFromMinutes },
                    {
                        id: PrPayslipEmploymentSubitemTypeCode.CzPaidTimeOffSummerCampHourlyCompensation,
                        valueType: RowValueType.Currency
                    }
                ]
            },
            {
                id: PrPayslipEmploymentItemTypeCode.UnpaidAbsences,
                label: () => getEnumDisplayValue(EntityTypeName.PrPayslipEmploymentItemType, PrPayslipEmploymentItemTypeCode.UnpaidAbsences),
                isHeader: true,
                formatter: () => "", /* Always Zero, do not show */
                visibleIfContains: [
                    PrPayslipEmploymentItemTypeCode.LegislativeAbsenceUnpaid,
                    PrPayslipEmploymentItemTypeCode.CustomAbsenceUnpaid,
                    PrPayslipEmploymentItemTypeCode.IllnessUnpaid
                ]
            },
            {
                label: getRowHeaderLabel("Name"),
                value: getRowHeaderLabel("Hours"),
                isHeader: true,
                isSubRow: true,
                visibleIfContains: [
                    PrPayslipEmploymentItemTypeCode.LegislativeAbsenceUnpaid,
                    PrPayslipEmploymentItemTypeCode.CustomAbsenceUnpaid,
                    PrPayslipEmploymentItemTypeCode.IllnessUnpaid
                ]
            },
            {
                id: [
                    PrPayslipEmploymentItemTypeCode.LegislativeAbsenceUnpaid,
                    PrPayslipEmploymentItemTypeCode.CustomAbsenceUnpaid,
                    PrPayslipEmploymentItemTypeCode.IllnessUnpaid
                ],
                isSubRow: true,
                valueType: RowValueType.HoursFromMinutes,
            },
            // **** Extra Salaries ****
            {
                id: PrPayslipEmploymentItemTypeCode.ExtraSalaryTotalWithoutEmergency,
                isHeader: true,
                visibleIfContains: [
                    ...ExtraSalaryBaseItemTypes,
                    PrPayslipEmploymentItemTypeCode.ExtraSalaryHourlyPercentage,
                    PrPayslipEmploymentItemTypeCode.ExtraSalaryHourly,
                    PrPayslipEmploymentItemTypeCode.ExtraSalaryShift,
                    PrPayslipEmploymentItemTypeCode.Emergency
                ]
            },
            {
                ...getHeaderRowDef(["Count", "Value"], [
                    ...ExtraSalaryBaseItemTypes,
                    PrPayslipEmploymentItemTypeCode.ExtraSalaryHourlyPercentage,
                    PrPayslipEmploymentItemTypeCode.ExtraSalaryHourly,
                    PrPayslipEmploymentItemTypeCode.ExtraSalaryShift,
                    PrPayslipEmploymentItemTypeCode.Emergency
                ]),
                value: getRowHeaderLabel("Amount")
            },
            {
                id: [
                    ...ExtraSalaryBaseItemTypes,
                    PrPayslipEmploymentItemTypeCode.ExtraSalaryHourlyPercentage
                ],
                subItems: [
                    { formatter: extraSalaryCountFormatter, valueType: RowValueType.HoursFromMinutes },
                    {
                        id: [
                            PrPayslipEmploymentSubitemTypeCode.CzOvertimeValue,
                            PrPayslipEmploymentSubitemTypeCode.CzNightValue,
                            PrPayslipEmploymentSubitemTypeCode.CzWeekendValue,
                            PrPayslipEmploymentSubitemTypeCode.CzEmergencyValue,
                            PrPayslipEmploymentSubitemTypeCode.CzHarshWorkEnvValue,
                            PrPayslipEmploymentSubitemTypeCode.CzHolidayValue,
                            PrPayslipEmploymentSubitemTypeCode.CzCustHourPercValue,
                        ],
                        formatter: extraSalaryPercentageFormatter,
                        valueType: RowValueType.Percentage
                    }
                ],
                valueType: RowValueType.Currency
            },
            {
                id: PrPayslipEmploymentItemTypeCode.ExtraSalaryHourly,
                subItems: [
                    { formatter: extraSalaryCountFormatter, valueType: RowValueType.HoursFromMinutes },
                    {
                        id: PrPayslipEmploymentSubitemTypeCode.CzCustHourAbsValue,
                        valueType: RowValueType.Currency
                    }
                ],
                valueType: RowValueType.Currency
            },
            {
                id: PrPayslipEmploymentItemTypeCode.ExtraSalaryShift,
                subItems: [
                    { formatter: extraSalaryAbsValueFormatter, valueType: RowValueType.String },
                    {
                        id: PrPayslipEmploymentSubitemTypeCode.CzCustShiftAbsValue,
                        valueType: RowValueType.Currency
                    }
                ],
                valueType: RowValueType.Currency
            },
            { id: PrPayslipEmploymentItemTypeCode.Emergency, isHeader: true },

            // **** Benefits ****
            { id: PrPayslipEmploymentItemTypeCode.BenefitsTotal, isHeader: true },
            {
                id: [
                    PrPayslipEmploymentItemTypeCode.MealVouchers,
                    PrPayslipEmploymentItemTypeCode.LumpSumMealBenefit,
                ],
                headerRow: getHeaderRowDef(["Contribution", "Unit"]),
                isSubRow: true,
                subItems: [
                    {
                        id: [
                            PrPayslipEmploymentSubitemTypeCode.CzBenefitMealVoEmplr,
                            PrPayslipEmploymentSubitemTypeCode.CzBenefitMealLSEmplr
                        ]
                    },
                    {
                        id: [
                            PrPayslipEmploymentSubitemTypeCode.CzBenefitMealVoDays,
                            PrPayslipEmploymentSubitemTypeCode.CzBenefitMealLSDays,
                        ],
                        valueType: RowValueType.Days
                    }
                ]
            },
            {
                id: PrPayslipEmploymentItemTypeCode.RemoteWorkBenefit,
                isSubRow: true,
                subItems: [
                    { id: PrPayslipEmploymentSubitemTypeCode.CzBenefitRemoteWorkA },
                    {
                        id: PrPayslipEmploymentSubitemTypeCode.CzBenefitRemoteWorkH,
                        valueType: RowValueType.Hours
                    }
                ]
            },
            { id: PrPayslipEmploymentItemTypeCode.OtherBenefit, isSubRow: true },
            {
                label: i18next.t("Payslip:FormOverviews.LimitsOverview"),
                isHeader: true,
                formatter: () => "",
                isVisible: (storage, args) => {
                    return args.employment?.Items?.some(i => {
                        return limitItems.includes(i.TypeCode);
                    });
                }
            },
            {
                id: limitItems,
                headerRow: getHeaderRowDef([]),
                isSubRow: true,
            },
            { id: PrPayslipEmploymentItemTypeCode.SavingForRetirementTotal, isHeader: true },
            {
                id: [
                    PrPayslipEmploymentItemTypeCode.PensionRetirementSaving,
                    PrPayslipEmploymentItemTypeCode.LifeRetirementSaving,
                    PrPayslipEmploymentItemTypeCode.DIPBenefit,
                ],
                headerRow: {
                    ...getHeaderRowDef(["Subject"]),
                    label: getRowHeaderLabel("Type")
                },
                isSubRow: true,
                useTypeName: true,
                subItems: [
                    {
                        formatter: (item) => {
                            return (item as IPrPayslipEmploymentItemEntity).RetirementSaving?.RetirementSavingsProduct?.PrRetirementSavingsCompany?.Name;
                        }
                    }
                ]
            },
            { id: PrPayslipEmploymentItemTypeCode.TaxfreeSavingForRetirementTotal },
            { id: PrPayslipEmploymentItemTypeCode.TaxableSavingForRetirementTotal },
            { id: PrPayslipEmploymentItemTypeCode.CompanyCarForPrivateUse, isHeader: true },
            { id: PrPayslipEmploymentItemTypeCode.SocialInsuranceTotal, isHeader: true },
            {
                // header row for Employee and Employer social insurance
                label: getRowHeaderLabel("Name"),
                value: getRowHeaderLabel("Amount"),
                isHeader: true,
                isSubRow: true,
                subItems: [{
                    id: "Base",
                    value: getRowHeaderLabel("Base")
                }, {
                    id: "Size",
                    value: getRowHeaderLabel("Size")
                }, {
                    id: "Sale",
                    value: getRowHeaderLabel("Sale")
                }],
                filterSubItems: filterOutSaleSubitemsIfNotPresent,
                visibleIfContains: [PrPayslipEmploymentItemTypeCode.SocialInsurancePaidByEmployee, PrPayslipEmploymentItemTypeCode.SocialInsurancePaidByEmployer]
            },
            {
                id: PrPayslipEmploymentItemTypeCode.SocialInsurancePaidByEmployee,
                filterSubItems: filterOutSaleSubitemsIfNotPresent,
                subItems: [
                    {
                        formatter: socialInsuranceBaseFormatter,
                        valueType: RowValueType.Currency
                    },
                    {
                        id: PrPayslipEmploymentSubitemTypeCode.CzSocialInsEmployeePerc,
                        valueType: RowValueType.Percentage
                    },
                    {
                        id: PrPayslipEmploymentSubitemTypeCode.CzSocialInsEmployeeRetireeDiscPerc,
                        valueType: RowValueType.Percentage
                    }
                ]
            },
            {
                id: PrPayslipEmploymentItemTypeCode.SocialInsurancePaidByEmployer,
                filterSubItems: filterOutSaleSubitemsIfNotPresent,
                subItems: [
                    {
                        formatter: socialInsuranceBaseFormatter,
                        valueType: RowValueType.Currency
                    },
                    {
                        id: PrPayslipEmploymentSubitemTypeCode.CzSocialInsEmployerPerc,
                        valueType: RowValueType.Percentage
                    },
                    {
                        id: PrPayslipEmploymentSubitemTypeCode.CzSocialInsEmployerDiscountPerc,
                        valueType: RowValueType.Percentage
                    }
                ]
            },
        ]
    }
);
